import type { CSSProperties } from 'react'
import type { GalleryPhoto } from '@/types'

import { useEffect, useRef, useState } from 'react'
import { cn } from '@/lib/utils'

import {
    Pause,
    Play,
    RotateCcw
} from 'lucide-react'

import Photo from '@/components/landing/photo'

type ReelState = 'idle' | 'playing' | 'paused' | 'ended'

const SLIDE_MS = 4800
const PANS = [
    { x: '-1.5%', y: '-1%' },
    { x: '1.5%', y: '-1.5%' },
    { x: '-1%', y: '1%' },
    { x: '1.5%', y: '1%' }
]

interface MemoriesReelProps {
    photos: Array<GalleryPhoto>
}

/** Plays the gallery photos like a short film, story style, with captions. */
export default function MemoriesReel({ photos }: MemoriesReelProps) {
    const [state, setState] = useState<ReelState>('idle')
    const [index, setIndex] = useState(0)
    const containerRef = useRef<HTMLDivElement>(null)
    const total = photos.length
    const current = photos[Math.min(index, total - 1)]
    const running = state === 'playing' || state === 'paused'

    useEffect(() => {
        const node = containerRef.current

        if (!node || state !== 'playing' || typeof IntersectionObserver === 'undefined') return

        const observer = new IntersectionObserver(entries => {
            if (entries.some(entry => !entry.isIntersecting)) setState('paused')
        }, { threshold: 0.3 })

        observer.observe(node)

        return () => observer.disconnect()
    }, [state])

    if (!current) return null

    const start = () => {
        setIndex(0)
        setState('playing')
    }

    const next = () => {
        if (index >= total - 1) {
            setState('ended')

            return
        }

        setIndex(index + 1)
    }

    const previous = () => {
        setIndex(value => Math.max(0, value - 1))
    }

    const toggle = () => {
        setState(value => value === 'playing' ? 'paused' : 'playing')
    }

    const pan = PANS[index % PANS.length]

    const slideStyle = {
        '--slide-duration': `${SLIDE_MS}ms`,
        '--pan-x': pan.x,
        '--pan-y': pan.y
    } as CSSProperties

    return (
        <div
            ref={containerRef}
            className='relative aspect-[4/5] w-full overflow-hidden rounded-[2rem] bg-night shadow-[0_40px_90px_-40px_rgba(232,84,124,0.55)] ring-1 ring-white/10 sm:aspect-video'
        >
            <Photo
                key={`backdrop-${current.id}`}
                src={current.photo ?? ''}
                alt=''
                rotation={current.photo_rotation}
                className='absolute inset-0 scale-125 opacity-45 blur-2xl'
            />

            <div key={`slide-${current.id}`} className='absolute inset-0 animate-in fade-in duration-700'>
                <div
                    className={cn(
                        'absolute inset-0',
                        running && 'animate-ken-burns motion-reduce:animate-none',
                        state === 'paused' && '[animation-play-state:paused]'
                    )}
                    style={slideStyle}
                >
                    <Photo
                        src={current.photo ?? ''}
                        alt={current.photo_alt ?? ''}
                        rotation={current.photo_rotation}
                        fit='contain'
                        className='absolute inset-0'
                    />
                </div>
            </div>

            {state !== 'idle' && (
                <div aria-hidden className='absolute inset-x-4 top-4 z-20 flex gap-1.5'>
                    {photos.map((photo, position) => {
                        const done = position < index || state === 'ended'
                        const live = position === index && running

                        return (
                            <span key={photo.id} className='h-1 flex-1 overflow-hidden rounded-full bg-white/25'>
                                <span
                                    className={cn(
                                        'block h-full origin-left rounded-full bg-white',
                                        done && 'scale-x-100',
                                        live && 'animate-progress',
                                        !done && !live && 'scale-x-0',
                                        live && state === 'paused' && '[animation-play-state:paused]'
                                    )}
                                    style={live ? { '--slide-duration': `${SLIDE_MS}ms` } as CSSProperties : undefined}
                                    onAnimationEnd={live ? next : undefined}
                                />
                            </span>
                        )
                    })}
                </div>
            )}

            {running && (
                <div className='absolute inset-x-0 top-0 bottom-28 z-10 flex'>
                    <button
                        type='button'
                        onClick={previous}
                        aria-label='Pichli yaad'
                        className='h-full w-1/3 cursor-w-resize outline-none focus-visible:bg-white/5'
                    />

                    <button
                        type='button'
                        onClick={next}
                        aria-label='Agli yaad'
                        className='h-full w-2/3 cursor-e-resize outline-none focus-visible:bg-white/5'
                    />
                </div>
            )}

            {state !== 'idle' && state !== 'ended' && (
                <div className='absolute inset-x-0 bottom-0 z-10 bg-gradient-to-t from-night/90 via-night/45 to-transparent px-6 pt-20 pb-6 sm:px-9 sm:pb-8'>
                    {current.caption && (
                        <p
                            key={current.id}
                            data-mdlfy-cms={`gallery_photos:${current.id}:caption`}
                            className='max-w-xl animate-in font-hand text-2xl leading-tight text-cream duration-700 fade-in slide-in-from-bottom-2 sm:text-3xl'
                        >
                            {current.caption}
                        </p>
                    )}

                    <p className='mt-2 text-xs font-semibold tracking-[0.25em] text-cream/60'>
                        {index + 1} / {total}
                    </p>
                </div>
            )}

            {running && (
                <button
                    type='button'
                    onClick={toggle}
                    aria-label={state === 'playing' ? 'Pause karo' : 'Play karo'}
                    className='absolute right-4 bottom-4 z-20 grid size-12 place-items-center rounded-full bg-white/15 text-cream ring-1 ring-white/25 backdrop-blur-md transition outline-none hover:bg-white/25 focus-visible:ring-4 focus-visible:ring-[#ffd1dc]/50 sm:right-6 sm:bottom-6'
                >
                    {state === 'playing'
                        ? <Pause aria-hidden className='size-5 fill-current' />
                        : <Play aria-hidden className='size-5 translate-x-px fill-current' />}
                </button>
            )}

            {state === 'idle' && (
                <div className='absolute inset-0 z-20 grid place-items-center bg-night/45'>
                    <button
                        type='button'
                        onClick={start}
                        className='group flex flex-col items-center gap-4 rounded-3xl p-4 text-cream outline-none focus-visible:ring-4 focus-visible:ring-[#ffd1dc]/50'
                    >
                        <span className='relative grid size-20 place-items-center rounded-full bg-gradient-to-br from-petal to-petal-deep shadow-[0_18px_50px_-12px_rgba(232,84,124,0.9)] transition-transform group-hover:scale-105 sm:size-24'>
                            <span aria-hidden className='absolute inset-0 animate-pulse-ring rounded-full bg-petal/60 motion-reduce:hidden' />

                            <Play aria-hidden className='relative size-8 translate-x-0.5 fill-current sm:size-10' />
                        </span>

                        <span className='text-lg font-bold tracking-wide'>
                            Yaadein play karo
                        </span>

                        <span className='-mt-3 text-sm text-cream/70'>
                            {total} pal, ek chhoti si film
                        </span>
                    </button>
                </div>
            )}

            {state === 'ended' && (
                <div className='absolute inset-0 z-20 grid animate-in place-items-center bg-night/70 px-6 text-center duration-700 fade-in'>
                    <div>
                        <p className='font-script text-5xl text-[#ffd1dc] sm:text-6xl'>
                            The End?
                        </p>

                        <p className='mx-auto mt-3 max-w-sm font-heading text-xl text-balance text-cream/90 italic'>
                            Bilkul nahi. Ye toh bas trailer tha, picture abhi baaki hai.
                        </p>

                        <button
                            type='button'
                            onClick={start}
                            className='mt-7 inline-flex items-center gap-2 rounded-full bg-cream px-6 py-3 font-bold text-wine shadow-lg transition outline-none hover:bg-white focus-visible:ring-4 focus-visible:ring-[#ffd1dc]/60'
                        >
                            <RotateCcw aria-hidden className='size-4' />
                            Phir se dekho
                        </button>
                    </div>
                </div>
            )}
        </div>
    )
}
